import {
    autoUpdate,
    flip,
    FloatingPortal,
    offset,
    Placement,
    size,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
} from '@floating-ui/react';
import React, { createContext, PropsWithChildren, ReactNode, useContext, useState } from 'react';
import styled, { css } from 'styled-components';

import { IconDots } from './icon';
import { HorizontalLayout, VerticalLayout } from './Layout';

interface DropdownCtx {
    openDropdown: () => void;
    closeDropdown: () => void;
    isOpen: boolean;
    setIsOpen: (b: boolean) => void;
}

const DropdownContext = createContext<DropdownCtx | null>(null);
export const useDropdownContext = () => useContext(DropdownContext);

const OFFSET = 10;

interface DropdownProps {
    dropdownTrigger?: JSX.Element | string;
    placement?: Placement;
    overflow?: string;
}

export const Dropdown = ({
    children,
    dropdownTrigger,
    placement = 'bottom-start',
    overflow,
}: PropsWithChildren<DropdownProps>) => {
    const [isOpen, setIsOpen] = useState(false);
    const openDropdown = () => setIsOpen(true);
    const closeDropdown = () => setIsOpen(false);

    const { refs, floatingStyles, context, strategy } = useFloating({
        placement,
        open: isOpen,
        onOpenChange: setIsOpen,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(OFFSET),
            flip(),
            size({
                apply: ({ availableHeight, elements }) => {
                    Object.assign(elements.floating.style, {
                        maxHeight: `${Math.max(50, availableHeight - OFFSET)}px`,
                    });
                },
            }),
        ],
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);

    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

    return (
        <DropdownContext.Provider value={{ openDropdown, closeDropdown, isOpen, setIsOpen }}>
            <HorizontalLayout
                {...getReferenceProps({ ref: refs.setReference })}
                aria-haspopup="true"
                aria-expanded={isOpen}
            >
                {dropdownTrigger}
            </HorizontalLayout>
            {isOpen && (
                <FloatingPortal>
                    <DropdownContent
                        {...getFloatingProps({
                            ref: refs.setFloating,
                            style: { ...floatingStyles, position: strategy },
                        })}
                        $overflow={overflow}
                    >
                        {children}
                    </DropdownContent>
                </FloatingPortal>
            )}
        </DropdownContext.Provider>
    );
};

const DropdownContent = styled.div<{ $overflow?: string }>`
    background-color: #fff;
    border-radius: 10px;
    border: 1px solid ${({ theme }) => theme.colors.cocoa200};
    box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.1);
    overflow: ${({ $overflow }) => $overflow ?? 'auto'};
    box-sizing: border-box;
`;

interface DropdownLayoutProps {
    top?: ReactNode;
    bottom?: ReactNode;
}

export const DropdownLayout = ({ children, top, bottom }: PropsWithChildren<DropdownLayoutProps>) => {
    return (
        <StyledVerticalLayout>
            {top ? <DropdownLayoutTop>{top}</DropdownLayoutTop> : null}
            <DropdownLayoutContent>{children}</DropdownLayoutContent>
            {bottom ? <DropdownLayoutBottom>{bottom}</DropdownLayoutBottom> : null}
        </StyledVerticalLayout>
    );
};

const layoutCss = css`
    position: sticky;
    background-color: #fff;
    z-index: 1;
    padding: 16px;
`;

const StyledVerticalLayout = styled(VerticalLayout)`
    width: 360px;
`;

const DropdownLayoutTop = styled.div`
    ${layoutCss}
    top: 0;
`;

const DropdownLayoutBottom = styled.div`
    ${layoutCss}
    bottom: 0;
`;

const DropdownLayoutContent = styled.div`
    padding: 0 16px;
`;

interface DropdownButton {
    button: JSX.Element | string;
    onClick: () => void;
}

export const DropdownButtonGroup = ({ buttons }: { buttons: DropdownButton[] }) => {
    const dropdownContext = useDropdownContext();
    return (
        <DropdownButtonGroupContainer>
            {buttons.map(({ onClick, button }, index) => (
                <StyledDropdownButton
                    key={index}
                    onClick={() => {
                        dropdownContext?.closeDropdown();
                        onClick();
                    }}
                >
                    {button}
                </StyledDropdownButton>
            ))}
        </DropdownButtonGroupContainer>
    );
};

const DropdownButtonGroupContainer = styled(VerticalLayout)`
    padding: 8px 0;
    min-width: 140px;
`;

const StyledDropdownButton = styled.button`
    all: unset;
    padding: 8px 16px;
    color: ${({ theme }) => theme.colors.cocoa900};
    text-align: center;
    font-size: 14px;
    font-weight: 300;
    cursor: pointer;

    &:hover {
        background: ${({ theme }) => theme.colors.cocoa0};
    }
`;

export const DropdownMenu = ({
    dropdownTrigger = <StyledDots size="medium" />,
    buttons,
    placement,
}: {
    placement?: Placement;
    buttons: DropdownButton[];
    dropdownTrigger?: JSX.Element | string;
}) => {
    return (
        <Dropdown dropdownTrigger={dropdownTrigger} placement={placement}>
            <DropdownButtonGroup buttons={buttons} />
        </Dropdown>
    );
};

const StyledDots = styled(IconDots)`
    padding: 0.25rem;
    border-radius: 50%;
    border: 1px solid ${({ theme }) => theme.colors.cocoa200};
    cursor: pointer;
`;
